/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Smarts Conversation UI
 *| (C) Copyright IBM Corp. 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

import * as tf from '@tensorflow/tfjs'
import {
	BATCH_SIZE,
	EPOCHS,
	MIN_NN_SIZE,
	MAX_NN_SIZE,
	DEFAULT_DENSE_LAYERS,
	LEARNING_RATE
} from '../utils/Constants'

export default class RegressionModel {
	/**
	 * Tensorflow model for client side regression 
	 *
	 * @param config {Object}: model config paramaters
	 * @constructor
	 */
	constructor(config) {
		if (config === undefined) {
			throw new Error('Config must be present')
		}

		// model config
		this.layers = config.layers || DEFAULT_DENSE_LAYERS
		this.batchSize = config.batchSize || BATCH_SIZE
		this.epochs = config.epochs || EPOCHS
		this.learningRate = config.learningRate || LEARNING_RATE
		this.xNormalizer = config.xNormalizer
		this.yNormalizer = config.yNormalizer
		this.encoder = config.encoder
		this.callbacks = config.callbacks || tf.callbacks.earlyStopping({
			monitor: 'val_loss',
			patience: 20,
			restore_best_weights: true
		})
	}

	/**
	 * Build and train model
	 * Inputs (x & y) are expected to already be encoded and normized
	 *
	 * @param {tf.tensor} x: 2D tensor, each tensor is 1 row
	 * @param {tf.tensor} y: 1D tensor for model to predict
	 * @returns {Object} model history
	 */
	buildModelAndTrain(xTrain, yTrain, xVal, yVal) {
		this.model = this.buildModel(xTrain.shape[1])

		return this.model.fit(xTrain, yTrain, {
			batchSize: this.batchSize,
			epochs: this.epochs,
			validationData: [xVal, yVal],
			callbacks: this.callbacks
		}).then(history => history.history)
	}

	/**
	 * Build model
	 *
	 * @param {number} inputShape: shape of input dim
	 * @returns {tf.sequential()} Tensorflow model based on config
	 */
	buildModel(inputShape) {
		if (!inputShape) {
			throw new Error('InputShape missing')
		} 

		// Build entry config
		const model = tf.sequential()
		let units = Math.min(MAX_NN_SIZE, Math.max(Math.round(this.layers.nnSizeFactor * inputShape), MIN_NN_SIZE))

		// add input layer
		model.add(tf.layers.dense({
			inputShape,
			units,
			activation: this.layers.activation,
			kernelInitializer: 'randomNormal'
		}))

		// Build hidden layers
		model.add(tf.layers.dense({
			units,
			activation: this.layers.activation
		}))

		// Output layer
		model.add(tf.layers.dense({
			units: 1,
			activation: 'linear'
		}))

		// Configure model for training
		let compileConfig = {
			optimizer: tf.train.adam(this.learningRate),
			loss: 'meanSquaredError'
		}

		// Compiling the model
		model.compile(compileConfig)
		
		return model
	}

	/**
	 * Use model to make prediction on new data
	 *
	 * @param {Array.<Array.<number|boolean|string>>} x: data point(s) to input to model
	 * @returns {number} predicted y
	 */
	predict(x, encoded = false) {
		if (!('model' in this)) {
			throw new Error('Can not make prediction without model')
		}

		const xEncoded = encoded ? x : this.encoder.encode(x)
		const xNorm = this.xNormalizer.transform(tf.tensor(xEncoded))
		
		const yPredNorm = this.model.predict(xNorm)
		const yPred = this.yNormalizer.inverseTransform(yPredNorm)
		
		return yPred.dataSync()
	}
}
